<template>
  <v-dialog
    :value="value"
    max-width="1200"
    @input="handleDialogInput"
  >
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center flex-wrap">
        <span class="text-h6">Book an appointment</span>
        <v-btn icon aria-label="Close" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-0">
        <div v-if="embedError" class="pa-6 text-center">
          <p class="body-2 mb-4">
            The scheduler could not be embedded here. Open Calendly in a new tab to pick a time.
          </p>
          <v-btn color="primary" @click="openExternal">
            Open Calendly
            <v-icon right small>mdi-open-in-new</v-icon>
          </v-btn>
        </div>
        <div
          v-show="!embedError"
          ref="calendlyHost"
          class="calendly-book-host"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Fallback when task `link` is missing, invalid, or not a Calendly URL (Manage: set `link` to a full https://…calendly.com/… URL).
 */
export const DEFAULT_CALENDLY_BOOK_APPOINTMENT_URL = 'https://calendly.com/clozings/buyers';
const CALENDLY_SCRIPT_SRC = 'https://assets.calendly.com/assets/external/widget.js';

export default {
  name: 'BookAppointmentDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    /** Calendly scheduling URL (task `link` from Manage when it is a calendly.com URL). */
    calendlyUrl: {
      type: String,
      default: DEFAULT_CALENDLY_BOOK_APPOINTMENT_URL,
    },
  },
  data() {
    return {
      embedError: false,
      scheduledThisSession: false,
      /** Incremented on every mount attempt; stale async runs abort when they see a newer generation. */
      mountGen: 0,
    };
  },
  watch: {
    value(val) {
      if (val) {
        this.embedError = false;
        this.scheduledThisSession = false;
        this.mountCalendly();
      } else {
        this.teardownCalendly();
      }
    },
    calendlyUrl() {
      if (this.value) {
        this.embedError = false;
        this.mountCalendly();
      }
    },
  },
  mounted() {
    window.addEventListener('message', this.handleCalendlyMessage);
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleCalendlyMessage);
  },
  methods: {
    handleDialogInput(nextValue) {
      if (!nextValue && this.value && !this.scheduledThisSession) {
        this.$emit('closed');
      }
      this.$emit('input', nextValue);
    },
    close() {
      this.$emit('closed');
      this.$emit('input', false);
    },
    /**
     * Handles Calendly postMessage events for reliable booking confirmation.
     * 
     */
    handleCalendlyMessage(event) {
      const origin = String(event?.origin || '');
      const isCalendlyOrigin = origin === 'https://calendly.com' || origin.endsWith('.calendly.com');
      if (!isCalendlyOrigin) {
        return;
      }
      const eventName = event?.data?.event;
      if (eventName !== 'calendly.event_scheduled' || this.scheduledThisSession) {
        return;
      }
      this.scheduledThisSession = true;
      this.$emit('scheduled', event?.data?.payload || null);
      this.$emit('input', false);
    },
    openExternal() {
      window.open(this.calendlyUrl, '_blank', 'noopener,noreferrer');
    },
    teardownCalendly() {
      const host = this.$refs.calendlyHost;
      if (host) {
        host.innerHTML = '';
      }
    },
    loadCalendlyScript() {
      return new Promise((resolve, reject) => {
        if (window.Calendly) {
          resolve();
          return;
        }
        const existing = document.querySelector(`script[src="${CALENDLY_SCRIPT_SRC}"]`);
        if (existing) {
          if (window.Calendly) {
            resolve();
            return;
          }
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', () => reject(new Error('Calendly script failed')));
          return;
        }
        const script = document.createElement('script');
        script.src = CALENDLY_SCRIPT_SRC;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Calendly script failed'));
        document.head.appendChild(script);
      });
    },
    /**
     * Mounts the Calendly inline widget. Uses a generation counter so that when both the
     * `value` and `calendlyUrl` watchers fire in the same tick (e.g. on first open), only
     * the last-started invocation proceeds to call initInlineWidget — preventing duplicates.
     *
     */
    async mountCalendly() {
      const gen = ++this.mountGen;

      await this.$nextTick();

      if (gen !== this.mountGen) {
        return;
      }

      const host = this.$refs.calendlyHost;
      if (!host) {
        return;
      }

      host.innerHTML = '';

      try {
        await this.loadCalendlyScript();

        if (gen !== this.mountGen) {
          return;
        }

        if (!window.Calendly || typeof window.Calendly.initInlineWidget !== 'function') {
          throw new Error('Calendly API unavailable');
        }

        window.Calendly.initInlineWidget({
          url: this.calendlyUrl,
          parentElement: host,
        });

        this.embedError = false;
      } catch (e) {
        if (gen !== this.mountGen) {
          return;
        }
        console.error('Calendly embed failed:', e);
        this.embedError = true;
        host.innerHTML = '';
      }
    },
  },
};
</script>

<style scoped>
/* host div grows with the Calendly widget; no independent scroll */
.calendly-book-host {
  min-height: 700px;
  width: 100%;
  overflow: hidden;
}
</style>

<style>
/* let the iframe fill its natural height so only one scroll layer exists */
.calendly-book-host iframe {
  min-height: 700px;
  display: block;
}
</style>
